import { resolveFileUrl } from '@/utils/fileUrl';
import { getThemeFromDomain } from '@/utils/themeDomain';

export type PwaBrand = {
  name: string;
  shortName: string;
  logoUrl?: string | null;
};

type SizedIcons = {
  icon192: string;
  icon512: string;
  icon180: string;
};

const BRAND_CACHE_KEY = 'pwa_brand_v1';

let lastManifestObjectUrl: string | null = null;
let applySeq = 0;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (/^https?:\/\//i.test(src) && !src.startsWith(window.location.origin)) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Rasterize for apple-touch only. Returns a blob: object URL (not data:).
 * Web-manifest icons must stay on same-origin https paths — Chrome ignores data:/blob: icons
 * and will not fire beforeinstallprompt.
 */
async function rasterizeSquareBlobUrl(src: string, size: number): Promise<string> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unsupported');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const scale = Math.min(size / img.width, size / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
  return URL.createObjectURL(blob);
}

export function getDefaultPwaBrand(): PwaBrand {
  const theme = getThemeFromDomain();
  if (theme === 'kashly') {
    return { name: 'Kashly POS', shortName: 'Kashly', logoUrl: '/kashly-logo-en.png' };
  }
  return { name: 'SaleDesk POS', shortName: 'SaleDesk', logoUrl: '/pwa-icon-512.png' };
}

export function cachePwaBrand(brand: PwaBrand): void {
  try {
    sessionStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brand));
  } catch {
    /* private mode / quota */
  }
}

export function getCachedPwaBrand(): PwaBrand | null {
  try {
    const raw = sessionStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PwaBrand;
    if (!parsed?.name) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Cached tenant brand if any, else platform default (SaleDesk / Kashly). */
export function getEffectivePwaBrand(): PwaBrand {
  return getCachedPwaBrand() || getDefaultPwaBrand();
}

export function brandFromCompany(company: {
  company_name?: string | null;
  company_logo?: string | null;
}): PwaBrand {
  const defaults = getDefaultPwaBrand();
  const name = (company.company_name || '').trim() || defaults.name;
  const shortName = (name.split(/\s+/)[0] || defaults.shortName).slice(0, 12);
  return {
    name,
    shortName,
    logoUrl: company.company_logo || defaults.logoUrl,
  };
}

/** Same-origin PNG paths Chrome accepts for installability. */
export function getInstallIconPaths(theme: 'kashly' | 'saledesk' = getThemeFromDomain()): SizedIcons {
  if (theme === 'kashly') {
    return {
      icon192: '/kashly-logo-en.png',
      icon512: '/kashly-pwa-512.png',
      icon180: '/kashly-apple-touch-icon.png',
    };
  }
  return {
    icon192: '/pwa-icon-192.png',
    icon512: '/pwa-icon-512.png',
    icon180: '/apple-touch-icon.png',
  };
}

function setAppleTouchIcon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'apple-touch-icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

function setMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Absolute https URL on this origin (required inside blob: manifests). */
function sameOriginUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return new URL(path.startsWith('/') ? path : `/${path}`, window.location.origin).href;
}

/**
 * Apply PWA branding: dynamic web manifest + iOS apple-touch-icon.
 *
 * IMPORTANT: Manifest icons MUST be same-origin https PNG paths.
 * data: and blob: icon URLs make Chrome/Edge refuse beforeinstallprompt.
 * Company logos are applied to apple-touch-icon (iOS) and the in-app install UI only.
 */
export async function applyPwaBranding(brand?: Partial<PwaBrand>): Promise<PwaBrand> {
  const defaults = getDefaultPwaBrand();
  const name = (brand?.name || defaults.name).trim() || defaults.name;
  const shortName = (brand?.shortName || name.split(/\s+/)[0] || defaults.shortName).slice(0, 12);
  const logoRaw = brand?.logoUrl || defaults.logoUrl || '';
  const logo = logoRaw ? resolveFileUrl(logoRaw) : '';

  const resolved: PwaBrand = { name, shortName, logoUrl: logo || defaults.logoUrl };
  cachePwaBrand(resolved);

  const seq = ++applySeq;
  const installIcons = getInstallIconPaths();

  // iOS home-screen icon can use company logo (apple-touch is not part of Chromium install check)
  let appleTouch = sameOriginUrl(installIcons.icon180);
  if (logo) {
    try {
      appleTouch = await rasterizeSquareBlobUrl(logo, 180);
    } catch {
      try {
        appleTouch = sameOriginUrl(logo.startsWith('/') || /^https?:/i.test(logo) ? logo : installIcons.icon180);
      } catch {
        /* keep default */
      }
    }
  }

  if (seq !== applySeq) return resolved;

  const icon192 = sameOriginUrl(installIcons.icon192);
  const icon512 = sameOriginUrl(installIcons.icon512);

  const manifest = {
    name,
    short_name: shortName,
    description: `${name} — Cloud POS`,
    start_url: '/',
    scope: '/',
    id: '/',
    display: 'standalone' as const,
    orientation: 'any' as const,
    background_color: '#ffffff',
    theme_color: '#0f172a',
    icons: [
      { src: icon192, sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: icon512, sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: icon512, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
    ],
  };

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  const objectUrl = URL.createObjectURL(blob);
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (lastManifestObjectUrl) {
    try {
      URL.revokeObjectURL(lastManifestObjectUrl);
    } catch {
      /* ignore */
    }
  }
  lastManifestObjectUrl = objectUrl;
  link.href = objectUrl;

  setAppleTouchIcon(appleTouch);
  setMeta('apple-mobile-web-app-title', shortName);
  setMeta('application-name', shortName);
  document.title = name;

  return resolved;
}
